import React, { useState, useEffect } from 'react';
import ReactGA from 'react-ga4';
import Clarity from '@microsoft/clarity';
import './App.css';
import Navbar from './components/Navbar';
import HomePage from './components/HomePage';
import AboutPage from './components/AboutPage';
import HowToUsePage from './components/HowToUsePage';
import LoginPage from './components/LoginPage';
import UnifiedRankings from './components/UnifiedRankings';
import MindMapViewer from './components/MindMapViewer';
import PersonalDashboard from './components/PersonalDashboard';
import VideoComparison from './components/VideoComparison';
import AIChatAssistant from './components/AIChatAssistant';
import ChannelAnalysis from './components/ChannelAnalysis';
import ChannelComparison from './components/ChannelComparison';
import CommentsAnalysis from './components/CommentsAnalysis';
import VideoDetails from './components/VideoDetails';
import AdminDashboard from './components/AdminDashboard';
import SearchResultsPage from './components/SearchResultsPage';
import ReSEOPage from './components/ReSEOPage';
import DownloadExtensionPage from './components/DownloadExtensionPage';
import PromptBuilderAdminPage from './components/PromptBuilderAdminPage';

// Admin email check
const ADMIN_EMAILS = ['satyarth.prakash@adda247.com', 'ayush.chauhan@adda247.com'];
const isAdmin = (email) => {
  if (!email) return false;
  return ADMIN_EMAILS.some(adminEmail => adminEmail.toLowerCase() === email.toLowerCase());
};

function App() {
  const [currentPage, setCurrentPage] = useState('home');
  const [user, setUser] = useState(null);
  const [showLogin, setShowLogin] = useState(true); // Default to showing login
  const [selectedVideoId, setSelectedVideoId] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [currentVideoId, setCurrentVideoId] = useState(null);
  const [searchResultsData, setSearchResultsData] = useState({ query: '', results: null });

  // Initialize Google Analytics 4
  useEffect(() => {
    const measurementId = process.env.REACT_APP_GA4_MEASUREMENT_ID || 'G-KGNKPNH13W';
    
    ReactGA.initialize(measurementId, {
      gaOptions: {
        siteSpeedSampleRate: 100,
      },
      gtagOptions: {
        send_page_view: true,
      }
    });
    
    console.log('✅ Google Analytics 4 initialized');
  }, []);

  // Initialize Microsoft Clarity
  useEffect(() => {
    const clarityProjectId = process.env.REACT_APP_CLARITY_PROJECT_ID || 'tk9jq3osdn';
    
    if (clarityProjectId) {
      Clarity.init(clarityProjectId);
      console.log('✅ Microsoft Clarity initialized');
    }
  }, []);

  // Check for existing user on app load
  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      const userData = JSON.parse(savedUser);
      setUser(userData);
      setShowLogin(false); // Hide login if user exists
      
      // Identify user in Clarity for better tracking
      if (userData.username) {
        Clarity.identify(userData.username, undefined, undefined, userData.username);
      }
      
      // Set user ID in GA4
      if (userData.username) {
        ReactGA.set({ userId: userData.username });
      }
    } else {
      setShowLogin(true); // Show login if no user found
    }
  }, []);

  // Track page changes in GA4 and Clarity
  useEffect(() => {
    // Send page view to GA4
    ReactGA.send({ 
      hitType: "pageview", 
      page: `/${currentPage}`,
      title: currentPage.charAt(0).toUpperCase() + currentPage.slice(1)
    });
    
    // Track in Clarity
    Clarity.event(`page_view_${currentPage}`);
    Clarity.setTag('current_page', currentPage);
  }, [currentPage]);

  const handleLogin = (username) => {
    const userData = JSON.parse(localStorage.getItem('user'));
    setUser(userData);
    setShowLogin(false);
    
    // Track login event in GA4
    if (userData) {
      ReactGA.event({
        category: 'User',
        action: 'Login',
        label: userData.loginMethod || 'manual',
        value: userData.loginMethod === 'google' ? 1 : 0
      });
      
      // Set user ID based on login method
      if (userData.loginMethod === 'google') {
        ReactGA.set({ userId: userData.id });
      } else {
        ReactGA.set({ userId: userData.username });
      }
    }
    
    // Identify user in Clarity for session tracking
    if (userData) {
      const identifier = userData.loginMethod === 'google' ? userData.id : userData.username;
      const name = userData.loginMethod === 'google' ? userData.name : userData.username;
      
      Clarity.identify(identifier, undefined, undefined, name);
      Clarity.setTag('user_type', userData.loginMethod === 'google' ? 'google_user' : 'manual_user');
      Clarity.setTag('login_method', userData.loginMethod || 'manual');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    setUser(null);
  };

  const handlePageChange = (page, videoId = null) => {
    setCurrentPage(page);
    if (videoId) {
      setCurrentVideoId(videoId);
    }
  };

  const handleVideoClick = (videoId) => {
    setSelectedVideoId(videoId);
    setCurrentVideoId(videoId);
    setCurrentPage('video-details');
    
    // Track in GA4
    ReactGA.event({
      category: 'Leaderboard',
      action: 'Video Click',
      label: videoId
    });
    
    // Track leaderboard video click in Clarity
    Clarity.event('leaderboard_video_click');
    Clarity.setTag('clicked_video_id', videoId);
  };

  const handleSearchVideoSelect = (url) => {
    // Extract video ID from URL
    const videoIdMatch = url.match(/(?:v=|\/)([a-zA-Z0-9_-]{11})/);
    const videoId = videoIdMatch ? videoIdMatch[1] : null;
    
    // Set the video ID to trigger auto-analysis in HomePage
    if (videoId) {
      setSelectedVideoId(videoId);
    }
    
    // Also dispatch custom event for immediate analysis
    const event = new CustomEvent('search-video-selected', { detail: { url } });
    window.dispatchEvent(event);
    
    // Navigate to home page
    setCurrentPage('home');
  };

  const handleSearchChannelSelect = (url) => {
    // Navigate to channel analysis first
    setCurrentPage('channel-analysis');
    // Dispatch event after navigation to ensure component is mounted
    setTimeout(() => {
      const event = new CustomEvent('analyze-channel', { detail: { url } });
      window.dispatchEvent(event);
    }, 50);
  };

  const handleOpenSearchPage = ({ query, results }) => {
    setSearchResultsData({ query, results });
    setCurrentPage('search');
  };

  const handleAnalyzeFromSearch = (url, type = 'video') => {
    if (type === 'video') {
      handleSearchVideoSelect(url);
    } else if (type === 'channel') {
      handleSearchChannelSelect(url);
    }
  };

  const renderPage = () => {
    if (showLogin || !user) {
      return <LoginPage onLogin={handleLogin} />;
    }

    switch (currentPage) {
      case 'home':
        return <HomePage onPageChange={setCurrentPage} selectedVideoId={selectedVideoId} onAnalysisComplete={setAnalysisResult} />;
      case 'dashboard':
        return <PersonalDashboard user={user} />;
      case 'comparison':
        return <VideoComparison user={user} />;
      case 'mind-map':
        // Track Mind Map access
        if (analysisResult) {
          ReactGA.event({
            category: 'Feature',
            action: 'Mind Map Opened',
            label: 'Navigation'
          });
        }
        return <MindMapViewer analysisResult={analysisResult} />;
      case 'leaderboard':
      case 'category-rankings':
        ReactGA.event({
          category: 'Feature',
          action: 'Rankings Opened',
          label: 'Navigation'
        });
        return <UnifiedRankings onVideoClick={handleVideoClick} />;
      case 'about':
        return <AboutPage />;
      case 'comments-analysis':
        // Track Comments Analysis access
        ReactGA.event({
          category: 'Feature',
          action: 'Comments Analysis Opened',
          label: 'Navigation'
        });
        return <CommentsAnalysis onPageChange={setCurrentPage} user={user} />;
      case 'channel-analysis':
        ReactGA.event({
          category: 'Feature',
          action: 'Channel Analysis Opened',
          label: 'Navigation'
        });
        return <ChannelAnalysis onPageChange={handlePageChange} />;
      case 'video-details':
        return <VideoDetails videoId={currentVideoId} onBack={() => handlePageChange('channel-analysis')} />;
      case 'channel-comparison':
        ReactGA.event({
          category: 'Feature',
          action: 'Channel Comparison Opened',
          label: 'Navigation'
        });
        return <ChannelComparison onPageChange={setCurrentPage} />;
      case 'admin':
        if (!isAdmin(user?.email)) {
          return (
            <div style={{
              minHeight: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '80px 20px 40px',
              background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a3a 100%)'
            }}>
              <div style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '2px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '16px',
                padding: '2rem',
                textAlign: 'center'
              }}>
                <h2 style={{ color: '#ef4444', marginBottom: '1rem' }}>Access Denied</h2>
                <p style={{ color: '#e5e7eb' }}>Admin privileges required</p>
              </div>
            </div>
          );
        }
        return <AdminDashboard user={user} />;
      case 'search':
        return (
          <SearchResultsPage
            searchData={searchResultsData}
            onSearch={handleOpenSearchPage}
            onAnalyzeVideo={(url) => handleAnalyzeFromSearch(url, 'video')}
            onAnalyzeChannel={(url) => handleAnalyzeFromSearch(url, 'channel')}
          />
        );
      case 're-seo':
        ReactGA.event({
          category: 'Feature',
          action: 'Re-SEO Opened',
          label: 'Navigation'
        });
        return <ReSEOPage />;
      default:
        return <HomePage />;
    }
  };

  if (window.location.pathname === '/prompt-builder-download/admin') {
    return <PromptBuilderAdminPage />;
  }

  if (window.location.pathname === '/prompt-builder-download') {
    return <DownloadExtensionPage />;
  }

  return (
    <div className="App">
      {user && (
        <>
          <Navbar 
            currentPage={currentPage} 
            onPageChange={setCurrentPage} 
            user={user}
            onLogout={handleLogout}
            onShowLogin={() => setShowLogin(true)}
            onSearchVideoSelect={handleSearchVideoSelect}
            onSearchChannelSelect={handleSearchChannelSelect}
          onOpenSearchPage={handleOpenSearchPage}
            isAdmin={isAdmin(user?.email)}
          />
          <AIChatAssistant user={user} analysisResult={analysisResult} />
        </>
      )}
      {renderPage()}
    </div>
  );
}

export default App;
